// The war's VFX layer (docs/ARCHITECTURE.md §8, docs/ART-DIRECTION.md §7): sim events from the MatchView play
// registry effects through seven pooled primitives, all on the emissive layer with additive blending. Weapon attacks
// (MatchView.onAttacks) play each shooter's attack style (attacks.ts): projectiles in the ribbon pool plus muzzle and
// impact effects, placed on the glyphs of the shooter and its target (render/units/glyphSizing.ts).
//
// Draw calls (worst case, every pool live): rings 1 + ribbons 1 + particles 1 + labels 1 + one route-sweep mesh per
// scaled captureEdgeSweep slot (≤ 24 at High) = 4 + scaled route sweeps.
// update() allocates nothing; event handling allocates only map entries for newly followed entities.

import { Group, Vector3 } from 'three'
import { InvariantViolation, NEUTRAL_OWNER, type OwnerValue, type SimEvent, type SimId, type StrikeTypeId } from '@taxwar/net'
import type { AttackView, Combatant, MatchView, StrikeFlightOut, VfxLayer } from '../match/contracts'
import { warCapacity } from '../match/warSetup'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../render/geo/globeSpace'
import { glyphWorldSize, transportGlyphAltitude, transportGlyphSize, unitGlyphAltitude, unitGlyphSize } from '../render/units/glyphSizing'
import type { Unsubscribe } from '../net'
import type { Palette } from '../render/palette'
import type { QualityTier } from '../render/visualSettings'
import { createGlyphAtlas, type GlyphAtlas } from './atlas'
import { attackStyleOf, playVolley, splashMuOf, Volley, type VolleyOutput } from './attacks'
import { bindingsOf, bookkeepingOf, placeSightedFlight, type BindingWorld, type EffectPlayer, type ErasedBinding } from './bindings'
import { VfxPalette } from './colours'
import {
  PRIMITIVE_PLACEMENT,
  effectDef,
  flashOnsetDelay,
  type DefOf,
  type EffectDef,
  type EffectId,
  type EffectIdFor,
  type Primitive,
  type PrimitivePlacement,
} from './effects'
import { tangentAt } from './paths'
import { FLIGHT_CAPACITY, FLIGHT_RIBBONS, FlightSystem } from './FlightSystem'
import { WarGeography } from './geography'
import { LabelSystem } from './LabelSystem'
import { ParticleSystem } from './ParticleSystem'
import { createPlacements, type FlightPlacement, type PathPlacement, type PlacementByKind, type PlacementKind, type PointPlacement, type RoutePlacement } from './placement'
import { LastKnownPositions } from './positions'
import { ProjectileSystem, type Launch, type ProjectileDef } from './ProjectileSystem'
import { RainSystem } from './RainSystem'
import { RibbonPool } from './RibbonPool'
import { RingSystem } from './RingSystem'
import { RouteTrailSystem } from './RouteTrailSystem'
import { QUALITY_SCALE, scaledCount } from './slots'
import { FlashBudget } from './timing'
import { TrailSystem, trailSlotCount } from './TrailSystem'
import { VfxUniforms } from './uniforms'

/** Projectiles in flight at High quality (a conscript volley is ten bolts). */
const PROJECTILE_SLOTS = 192
/** A structure's muzzle and a target building's aim point sit this high over the network, in glyph sizes. */
const STRUCTURE_MUZZLE_LIFT = 0.5
/** Shots land on the middle of a unit's glyph, this far above its base (glyph sizes). */
const TARGET_AIM_LIFT = 0.4
/** A structure's glyph size relative to a size-1 unit glyph. */
const STRUCTURE_GLYPH_SIZE = 1
const MIN_TANGENT_LENGTH_SQ = 1e-12

type Placement = PlacementByKind[PlacementKind]
type PrimitivePlayers = { readonly [K in Primitive]: (id: EffectId, def: DefOf<K>, placement: PlacementByKind[PrimitivePlacement[K]]) => void }
type ErasedPlayer = (id: EffectId, def: EffectDef, placement: Placement) => void

export class WarVfxLayer implements VfxLayer, EffectPlayer {
  readonly root = new Group()

  private readonly palette: VfxPalette
  private readonly uniforms = new VfxUniforms()
  private readonly atlas: GlyphAtlas
  private readonly positions: LastKnownPositions
  private readonly rings: RingSystem
  private readonly ribbons: RibbonPool
  private readonly flights: FlightSystem
  private readonly trails: TrailSystem
  private readonly particles: ParticleSystem
  private readonly rain: RainSystem
  private readonly labels: LabelSystem
  private readonly geography: WarGeography
  private readonly routeTrails: RouteTrailSystem
  private readonly world: BindingWorld
  private readonly placements = createPlacements()
  private readonly flashes = new FlashBudget()
  private readonly projectiles: ProjectileSystem
  private readonly volley = new Volley()
  /** Render units per CSS pixel this frame (update's argument): sizes unit-scale effects like their glyphs. */
  private worldPerCssPixel = 0
  // Attack scratch.
  private readonly up = new Vector3()
  private readonly toward = new Vector3()
  /** Reused buffers for adopting strikes first seen mid-flight (no per-frame allocation). */
  private readonly strikeBuffer: SimId[] = []
  private readonly sighted: StrikeFlightOut = { owner: NEUTRAL_OWNER, strikeType: 0 as StrikeTypeId, originLat: 0, originLon: 0, targetLat: 0, targetLon: 0, flying: false }
  private readonly unsubscribe: readonly Unsubscribe[]
  /** Layer clock, seconds since creation. */
  private now = 0
  private disposed = false

  private readonly players: PrimitivePlayers = {
    ring: (id, def, placement) => this.rings.play(id, def, placement, this.palette.colourOf(def.colour, placement.owner), this.now),
    burst: (_id, def, placement) => this.particles.burst(def.particle, def.count, placement, this.palette.colourOf(def.particle.colour, placement.owner)),
    rain: (id, def, placement) => this.rain.play(id, def, placement, this.palette.colourOf(def.particle.colour, placement.owner), this.now),
    label: (id, def, placement) => this.labels.play(id, def, placement, this.palette.colourOf(def.colour, placement.owner), this.now),
    trail: (id, def, placement) => this.trails.play(id, def, placement, this.palette.colourOf(def.colour, placement.owner), this.now),
    routeTrail: (id, def, placement) => this.routeTrails.play(id, placement, this.palette.colourOf(def.colour, placement.owner), this.now),
    flight: (_id, def, placement) => this.flights.play(def, placement, this.palette.colourOf(def.colour, placement.owner), this.now),
  }

  constructor(private readonly match: MatchView, quality: QualityTier, palette: Palette) {
    const scale = QUALITY_SCALE[quality]
    this.palette = new VfxPalette(match.you, palette)
    this.atlas = createGlyphAtlas()
    // Every unit the viewer can see deployed, plus every strike in flight.
    this.positions = new LastKnownPositions(match, warCapacity(match.setup).units + FLIGHT_CAPACITY)
    this.rings = new RingSystem(this.uniforms, scale)
    const flightSlots = FLIGHT_CAPACITY * FLIGHT_RIBBONS
    const trailSlots = trailSlotCount(scale)
    const projectileSlots = scaledCount(PROJECTILE_SLOTS, scale)
    this.ribbons = new RibbonPool(flightSlots + trailSlots + projectileSlots, this.uniforms, this.palette.hue('hot'))
    this.flights = new FlightSystem(this.ribbons, 0, this.positions, match)
    this.trails = new TrailSystem(this.ribbons, flightSlots, scale)
    this.projectiles = new ProjectileSystem(this.ribbons, flightSlots + trailSlots, projectileSlots, this.handleImpact)
    this.particles = new ParticleSystem(this.atlas, this.uniforms, scale)
    this.rain = new RainSystem(this.particles, scale)
    this.labels = new LabelSystem(this.atlas, this.uniforms, scale)
    this.geography = new WarGeography(match.setup)
    this.routeTrails = new RouteTrailSystem(this.uniforms, this.palette.hue('hot'), scale)
    this.world = { match, geography: this.geography, positions: this.positions, flights: this.flights }
    // Every part is additive and depth-read-only, so draw order does not change the composite.
    this.root.add(this.rings.pool.mesh, this.ribbons.mesh, this.particles.pool.mesh, this.labels.pool.mesh, this.routeTrails.root)
    this.unsubscribe = [match.onEvents(this.handleEvents), match.onAttacks(this.handleAttacks)]
  }

  update(dtSeconds: number, worldPerCssPixel: number): void {
    if (this.disposed) throw new InvariantViolation('VFX layer updated after dispose')
    this.now += dtSeconds
    this.worldPerCssPixel = worldPerCssPixel
    this.uniforms.advance(dtSeconds)
    this.particles.beginFrame()
    this.positions.refresh()
    this.adoptSightedStrikes()
    this.flights.update(this.now)
    this.trails.update(this.now)
    this.routeTrails.update(this.now)
    this.projectiles.update(this.now, worldPerCssPixel)
    this.ribbons.upload()
    this.rings.update(this.now)
    this.rain.update(this.now, dtSeconds)
    this.particles.update(dtSeconds)
    this.labels.update(this.now)
  }

  /** Strikes that entered sight without a strikeLaunched event (enemy launches out of sight) still get a ribbon. */
  private adoptSightedStrikes(): void {
    const count = this.match.visibleStrikes(this.strikeBuffer)
    for (let i = 0; i < count; i++) {
      const strike = this.strikeBuffer[i] as SimId
      if (this.flights.follows(strike)) continue
      if (!this.match.strikeFlight(strike, this.sighted) || !this.sighted.flying) continue
      this.flight(placeSightedFlight(strike, this.sighted, this.placements.flight), this.placements.flight)
    }
  }

  /** ART-DIRECTION §4: removes glitch / pixel-sort jitter, paper flicker and the damage-static animation. */
  setReduceEffects(reduce: boolean): void {
    this.uniforms.setReduceEffects(reduce)
  }

  /**
   * ART-DIRECTION §3: rewrites owner and fixed hues in place (no pool is reallocated). Flights, trails, rain and ribbon
   * cores recolour at once; one-shot effects already playing keep their hue for their remaining (short) life.
   */
  setPalette(palette: Palette): void {
    this.palette.setPalette(palette)
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    for (const unsubscribe of this.unsubscribe) unsubscribe()
    this.root.removeFromParent()
    this.root.clear()
    this.rings.dispose()
    this.ribbons.dispose()
    this.particles.dispose()
    this.labels.dispose()
    this.routeTrails.dispose()
    this.geography.dispose()
    this.atlas.texture.dispose()
  }

  point(ids: readonly EffectIdFor<'point'>[], placement: PointPlacement): void {
    this.playAll(ids, placement)
  }

  path(ids: readonly EffectIdFor<'path'>[], placement: PathPlacement): void {
    this.playAll(ids, placement)
  }

  route(ids: readonly EffectIdFor<'route'>[], placement: RoutePlacement): void {
    this.playAll(ids, placement)
  }

  flight(ids: readonly EffectIdFor<'flight'>[], placement: FlightPlacement): void {
    this.playAll(ids, placement)
  }

  private playAll(ids: readonly EffectId[], placement: Placement): void {
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i] as EffectId
      const def = effectDef(id)
      if (PRIMITIVE_PLACEMENT[def.primitive] !== placement.kind) throw new InvariantViolation(`effect ${id} played at a ${placement.kind} placement`)
      const flashDelay = flashOnsetDelay(def)
      if (flashDelay !== null && !this.flashes.admit(this.now + flashDelay)) continue
      ;(this.players[def.primitive] as ErasedPlayer)(id, def, placement)
    }
  }

  // -------------------------------------------------------------------------------------------- attacks

  private readonly volleyOutput: VolleyOutput = {
    launch: (launch: Launch) => {
      const def = launch.def
      if (def === null) throw new InvariantViolation('projectile launched without a def')
      launch.colour = this.palette.colourOf(def.colour, launch.owner)
      this.projectiles.launch(launch, this.now)
    },
    point: (ids, owner, at) => this.pointAt(ids, owner, at, this.volley.unitSize),
  }

  private readonly handleAttacks = (attacks: readonly AttackView[]): void => {
    for (let i = 0; i < attacks.length; i++) {
      const attack = attacks[i] as AttackView
      this.aim(attack)
      playVolley(attackStyleOf(attack.shooterKind), this.volley, this.volleyOutput)
    }
  }

  /** A projectile landed: its impact effects, sized like a unit glyph this frame. */
  private readonly handleImpact = (def: ProjectileDef, owner: OwnerValue, at: Vector3): void => {
    if (def.impact.length > 0) this.pointAt(def.impact, owner, at, glyphWorldSize(STRUCTURE_GLYPH_SIZE, this.worldPerCssPixel))
  }

  /** Fills the volley for `attack` in world terms: muzzle and aim point on the two glyphs, axes, sizes. */
  private aim(attack: AttackView): void {
    const volley = this.volley
    const wpp = this.worldPerCssPixel
    volley.owner = attack.owner
    volley.unitSize = combatantSize(attack.shooterKind, wpp)
    volley.bodies = attack.bodies
    volley.time = this.now
    volley.splash = splashMuOf(attack.shooterKind) / this.world.geography.setup.muPerRadian
    latLonToVector3(attack.fromLat, attack.fromLon, muzzleAltitude(attack.shooterKind, wpp), volley.from)
    latLonToVector3(attack.toLat, attack.toLon, aimAltitude(attack.targetKind, wpp), volley.to)
    volley.range = volley.from.distanceTo(volley.to)
    // Axes in the shooter's tangent plane: its course, the line of fire (its course when the target is straight
    // below, as for a bomber over its target), and the right of the line of fire.
    const up = this.up.copy(volley.from).normalize()
    courseAt(up, attack.heading, volley.course)
    const toward = this.toward.copy(volley.to).addScaledVector(up, -up.dot(volley.to))
    if (toward.lengthSq() > MIN_TANGENT_LENGTH_SQ) volley.heading.copy(toward).normalize()
    else volley.heading.copy(volley.course)
    volley.side.crossVectors(up, volley.heading)
  }

  /** Point effects at world position `at`, `extent` wide. */
  private pointAt(ids: readonly EffectIdFor<'point'>[], owner: OwnerValue, at: Vector3, extent: number): void {
    const placement = this.placements.point
    placement.owner = owner
    placement.at.copy(at).normalize()
    placement.altitude = at.length()
    placement.extent = extent
    placement.amount = 0
    placement.seconds = 0
    this.point(ids, placement)
  }

  private readonly handleEvents = (events: readonly SimEvent[]): void => {
    for (let i = 0; i < events.length; i++) {
      const event = events[i] as SimEvent
      const bindings = bindingsOf(event.kind)
      for (let b = 0; b < bindings.length; b++) {
        const binding = bindings[b] as ErasedBinding
        const ids = binding.effects(event)
        if (ids.length === 0) continue
        binding.play(event, this.world, ids, this.placements[binding.placement], this)
      }
      const bookkeeping = bookkeepingOf(event.kind)
      if (bookkeeping !== undefined) bookkeeping(event, this.world, this.now)
    }
  }
}

// ------------------------------------------------------------------------------------------------ attack geometry

type CombatantOf<K extends Combatant['kind']> = Extract<Combatant, { readonly kind: K }>

/** World size of what fired or was hit, like its glyph this frame. */
const COMBATANT_SIZES: { readonly [K in Combatant['kind']]: (combatant: CombatantOf<K>, wpp: number) => number } = {
  unit: (combatant, wpp) => unitGlyphSize(combatant.unitType, wpp),
  city: (_combatant, wpp) => glyphWorldSize(STRUCTURE_GLYPH_SIZE, wpp),
  building: (_combatant, wpp) => glyphWorldSize(STRUCTURE_GLYPH_SIZE, wpp),
  transport: (combatant, wpp) => transportGlyphSize(combatant.mode, wpp),
}

/** Radius a shot leaves from: a unit's glyph base (aircraft fire from altitude), or a structure's top. */
const MUZZLE_ALTITUDES: { readonly [K in Combatant['kind']]: (combatant: CombatantOf<K>, wpp: number) => number } = {
  unit: (combatant, wpp) => unitGlyphAltitude(combatant.unitType, wpp),
  city: (_combatant, wpp) => GLOBE_ALTITUDE.network + STRUCTURE_MUZZLE_LIFT * glyphWorldSize(STRUCTURE_GLYPH_SIZE, wpp),
  building: (_combatant, wpp) => GLOBE_ALTITUDE.network + STRUCTURE_MUZZLE_LIFT * glyphWorldSize(STRUCTURE_GLYPH_SIZE, wpp),
  // A craft never fires, but the map is total; its muzzle is its glyph base (on the water, or in the air).
  transport: (combatant, wpp) => transportGlyphAltitude(combatant.mode, wpp),
}

/** Radius a shot lands at: the middle of a unit's or transport craft's glyph (so shots meet aircraft in the air), or the network. */
const AIM_ALTITUDES: { readonly [K in Combatant['kind']]: (combatant: CombatantOf<K>, wpp: number) => number } = {
  unit: (combatant, wpp) => unitGlyphAltitude(combatant.unitType, wpp) + TARGET_AIM_LIFT * unitGlyphSize(combatant.unitType, wpp),
  city: () => GLOBE_ALTITUDE.network,
  building: () => GLOBE_ALTITUDE.network,
  // Aim at the middle of the craft's glyph, matching how GlyphLayer draws it (a boat at the surface, a plane aloft).
  transport: (combatant, wpp) => transportGlyphAltitude(combatant.mode, wpp) + TARGET_AIM_LIFT * transportGlyphSize(combatant.mode, wpp),
}

function combatantSize(combatant: Combatant, wpp: number): number {
  return (COMBATANT_SIZES[combatant.kind] as (combatant: Combatant, wpp: number) => number)(combatant, wpp)
}

function muzzleAltitude(combatant: Combatant, wpp: number): number {
  return (MUZZLE_ALTITUDES[combatant.kind] as (combatant: Combatant, wpp: number) => number)(combatant, wpp)
}

function aimAltitude(combatant: Combatant, wpp: number): number {
  return (AIM_ALTITUDES[combatant.kind] as (combatant: Combatant, wpp: number) => number)(combatant, wpp)
}

const courseEast = new Vector3()
const courseNorth = new Vector3()

/** The unit tangent at `up` pointing along `heading` (radians clockwise from north). */
function courseAt(up: Vector3, heading: number, out: Vector3): Vector3 {
  tangentAt(up, courseEast)
  courseNorth.crossVectors(up, courseEast)
  return out.copy(courseNorth).multiplyScalar(Math.cos(heading)).addScaledVector(courseEast, Math.sin(heading)).normalize()
}
